import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/auth';
import { Calendar as CalendarIcon, ChevronRight, Loader2 } from 'lucide-react';

/**
 * Dashboard mini-widget — compact yearly heatmap for the Planning module.
 *
 * Renders a 53-week grid (same heat palette as the full Özet page) with the
 * current ISO week highlighted. Clicking any cell deep-links to the weekly
 * board. Admin-only; silently fetches `/planning/yearly-summary` on mount.
 */
export default function PlanningSummaryWidget({ year }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/planning/yearly-summary?year=${year}`);
        if (!cancelled) setData(res.data?.data || null);
      } catch {
        // silent — widget must not toast; keep it low-noise on the dashboard
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [year]);

  const totals = useMemo(() => {
    if (!data) return { assigned: 0, missing: 0, fill: 0 };
    let a = 0, m = 0;
    for (const w of data.weeks) { a += w.assigned; m += w.missing; }
    const denom = a + m;
    return {
      assigned: a,
      missing: m,
      fill: denom ? Math.round((a / denom) * 1000) / 10 : 0,
    };
  }, [data]);

  const nowWk = useMemo(() => {
    const d = new Date();
    const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = tmp.getUTCDay() || 7;
    tmp.setUTCDate(tmp.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
    return Math.ceil(((tmp - yearStart) / 86400000 + 1) / 7);
  }, []);

  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-xl p-5 hover:shadow-md transition-shadow" data-testid="dashboard-planning-widget">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/30">
            <CalendarIcon className="h-4 w-4 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Planlama Özeti</h3>
            <p className="text-[11px] text-slate-500">{year} · {totals.assigned} atama · {totals.fill}% doluluk</p>
          </div>
        </div>
        <Link
          to="/dashboard/planning/summary"
          className="text-xs text-emerald-600 hover:text-emerald-800 inline-flex items-center gap-0.5 font-semibold"
          data-testid="dashboard-planning-widget-all"
        >
          Tümü <ChevronRight className="h-3 w-3" />
        </Link>
      </div>

      {loading ? (
        <div className="h-20 flex items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        </div>
      ) : (
        <>
          <div className="grid gap-1" style={{ gridTemplateColumns: 'repeat(27, minmax(0, 1fr))' }} data-testid="dashboard-planning-widget-heatmap">
            {(data?.weeks || []).map((w) => {
              const isNow = w.week === nowWk;
              return (
                <Link
                  key={w.week}
                  to={`/dashboard/planning?year=${w.year}&week=${w.week}`}
                  className={`group aspect-square rounded ${heatColor(w.fill_rate)} ${isNow ? 'ring-2 ring-emerald-600' : ''} hover:scale-125 hover:ring-2 hover:ring-sky-400 transition-transform flex items-center justify-center`}
                  title={`W${w.week} — ${w.assigned} atama (${w.fill_rate}%)`}
                  data-testid={`dashboard-planning-widget-week-${w.week}`}
                >
                  <span className="text-[7px] font-bold text-slate-600 dark:text-slate-300 opacity-0 group-hover:opacity-100">
                    {w.week}
                  </span>
                </Link>
              );
            })}
          </div>
          <div className="flex items-center justify-between text-[10px] text-slate-400 mt-2">
            <span>Boş</span>
            <div className="flex items-center gap-0.5">
              {[0, 20, 40, 60, 80, 100].map((p) => (
                <span key={p} className={`inline-block h-2.5 w-2.5 rounded-sm ${heatColor(p)}`} />
              ))}
            </div>
            <span>Dolu</span>
          </div>
        </>
      )}
    </div>
  );
}

function heatColor(pct) {
  if (!pct) return 'bg-slate-200 dark:bg-slate-700';
  if (pct < 20) return 'bg-emerald-100 dark:bg-emerald-900/40';
  if (pct < 40) return 'bg-emerald-200 dark:bg-emerald-800';
  if (pct < 60) return 'bg-emerald-300 dark:bg-emerald-700';
  if (pct < 80) return 'bg-emerald-400 dark:bg-emerald-600';
  return 'bg-emerald-500 dark:bg-emerald-500';
}
